import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "services/ServiceUtil";
import { USER_PLAN_SERVICE_METHOD } from "services/commonService";
import { fetchCorpSavedFilters } from "./Filters.slice";
import { isEmpty } from "lodash";
import {
  filterPage,
  filterSliceInitialState,
  getDynamicSelectAllFlags,
  normalizeSelectAllFilters
} from "./FiltersHelper";

const SLICE = "GlobalFilters";

export const fetchDefaultFilters = createAsyncThunk(`${SLICE}/fetchDefaultFilters`, async (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getFilters, {}, thunkApi, [], { types: data?.types })
);

export const FiltersSlice = createSlice({
  name: SLICE,
  initialState: filterSliceInitialState,
  reducers: {
    setPageFilter: (state, action) => {
      const { pageName, searchText, isFilterApplied, ...filters } = action.payload;
      if ("defaultFilter" in filters) filters.defaultFilter = normalizeSelectAllFilters(filters.defaultFilter);
      if ("modifiedFilter" in filters) filters.modifiedFilter = normalizeSelectAllFilters(filters.modifiedFilter);
      const pageState = {
        ...state[pageName],
        ...filters,
        isFilterApplied: isFilterApplied ? isFilterApplied : filters?.isFilterApplied ? true : false,
        searchText: searchText
      };
      const appliedFilters =
        pageState.isFilterApplied || !isEmpty(pageState.modifiedFilter)
          ? pageState.modifiedFilter
          : pageState.defaultFilter;
      return {
        ...state,
        [pageName]: {
          ...pageState,
          dynamicSelectAll: getDynamicSelectAllFlags(appliedFilters)
        }
      };
    },

    resetPageModifiedFilter: (state, action) => {
      if (Array.isArray(action.payload.pageName)) {
        action.payload.pageName.map((key: any) => {
          state[key].modifiedFilter = [];
          state[key].searchText = "";
          state[key].isFilterApplied = false;
          state[key].dynamicSelectAll = getDynamicSelectAllFlags(state[key].defaultFilter);
        });
      } else {
        return {
          ...state,
          [action.payload.pageName]: {
            ...state[action.payload.pageName],
            modifiedFilter: [],
            searchText: "",
            isFilterApplied: false,
            dynamicSelectAll: getDynamicSelectAllFlags(state[action.payload.pageName]?.defaultFilter)
          }
        };
      }
    },
    resetAllModifiedFilters: (state) => {
      Object.keys(state).forEach((key) => {
        state[key].modifiedFilter = [];
        state[key].isFilterApplied = false;
        state[key].dynamicSelectAll = getDynamicSelectAllFlags(state[key].defaultFilter);
        state[key].searchText = "";
      });
    },
    resetAllFilters: (state) => {
      Object.keys(state).forEach((key) => {
        state[key].defaultFilter = [];
        state[key].modifiedFilter = [];
        state[key].isFilterApplied = false;
        state[key].dynamicSelectAll = {
          selectAllProperties: false,
          selectAllManagementCompanies: false,
          selectAllOwnerGroup: false,
          selectAllPlans: false
        };
        state[key].searchText = "";
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDefaultFilters.fulfilled, (state, action: any) => {
        // Loop through each key in the API response and update the Redux state dynamically
        const response = action.payload.other;
        return Object.keys(response).forEach((key) => {
          const filterData = response[key];
          const defaultFilter = normalizeSelectAllFilters(filterData[0].filters || []);
          if (state[key]) {
            // If the key already exists, update the defaultFilter and modifiedFilter dynamically
            state[key].defaultFilter = defaultFilter;
          } else {
            // Otherwise, initialize a new entry with defaultFilter and modifiedFilter
            state[key] = {
              defaultFilter,
              modifiedFilter: []
            };
          }
        });
      })
      .addCase(fetchCorpSavedFilters.fulfilled, (state, action: any) => {
        const response = action.payload?.default;
        const defaultFilter = normalizeSelectAllFilters(response?.[0]?.filters || []);
        state[filterPage.dashboardDisplay] = {
          defaultFilter,
          modifiedFilter: [],
          dynamicSelectAll: getDynamicSelectAllFlags(defaultFilter),
          isFilterApplied: false
        };
      });
  }
});

export const { setPageFilter, resetPageModifiedFilter, resetAllModifiedFilters, resetAllFilters } =
  FiltersSlice.actions;

export default FiltersSlice.reducer;
